import {
  SEAT_TAKEN_OVER_CLOSE_CODE,
  type BotDifficulty,
  type StartIntent,
  type TablePlayerIdentity,
  type TableStatusResponse,
} from "@bing/shared";
import { Client, MatchMakeError, type Room } from "colyseus.js";
import type { GameResult } from "./connection";
import { withConnectTickets } from "./connect-ticket";
import { isSeatMoved, SEAT_LOST, SEAT_MOVED, SeatMovedError, takeSeatBack } from "./seat-takeover";
import {
  manageRoom,
  ONLINE_CLOSE_WAIT_MS,
  ONLINE_REACQUIRE_TIMEOUT_MS,
  RECONNECT_TOO_EARLY_RETRY_MS,
  RECONNECT_TOO_EARLY_WINDOW_MS,
  ReconnectTooEarlyError,
  SeatExpiredError,
  type ConnectionRegistry,
} from "./lifecycle";
import { isRoomLocked } from "./join-error";
import { watchJoin } from "./join-watch";
import type { OnlineJoin, TableSettings } from "./private-table";
import { QUICK_MATCH } from "./private-table";
import { readRematchState, type RematchStateFrame } from "./rematch";

/**
 * colyseus's consented close (`CloseCode.CONSENTED` in colyseus.js, `Protocol.WS_CLOSE_CONSENTED` on
 * the game-server): the code a room closes with after the server processed our `leave()`.
 * `leave-when-live.test.ts` pins it to the client library's own value.
 */
export const CONSENTED_CLOSE_CODE = 4000;

/** One seat's occupant in the lobby (a human, a bot fill, or the private host). */
export interface LobbyPlayer {
  readonly seat: number;
  readonly name: string;
  readonly isBot: boolean;
  /** True for the private-table host (badged in the waiting room). */
  readonly isHost?: boolean;
  /** The occupant's LEAK-FREE public identity (app cuid + names + avatar refs),
   * broadcast by the server for humans it could resolve — powers the in-game
   * players panel (real avatar, `displayNameOf` name, friend actions). Bots and
   * unresolved humans omit it. */
  readonly user?: TablePlayerIdentity;
}

/**
 * A SEAT WHOSE PLAYER IS NOT THERE — a RESUMED game's other chair, still empty (2026-09-04).
 *
 * `user` is who that chair BELONGS to, resolved by the server from the app cuid the persisted
 * `OnlineGame` row seats by. The client has no other source for it: the empty chair is in no
 * roster (`players` lists occupants only) and the api's «بازی‌های در جریان» card carries no
 * opponent name — so without this field the seat renders as the SOLO default «ربات», which is a
 * lie until a bot really arrives. Absent when the server could not resolve them.
 */
export interface AbsentSeat {
  readonly seat: number;
  readonly user?: TablePlayerIdentity;
}

/** The waiting-room snapshot the server broadcasts before the game starts. */
export interface LobbyState {
  /** `"starting"` once the table is full/started and the countdown is running;
   * `"teamSelect"` while the host of a full-human partnership table (Hokm/
   * Shelem) arranges who partners with whom (seat parity IS the teams).
   *
   * ⚠️ `"playing"` IS A REAL VALUE ON THE WIRE AND WAS MISSING HERE UNTIL 2026-09-03. The server's
   * `TablePhase` is `"waiting" | "starting" | "teamSelect" | "playing"` and `lobbyPayload()` sends
   * `phase: this._phase` verbatim — it even branches on `this._phase !== "playing"` inside that same
   * function — so every lobby frame broadcast AFTER the deal (a takeover, a kick, a reclaim changing
   * the roster) carries it. `WaitingRoom` already reasoned about it in prose («the server flips the
   * phase to "playing" before it fills empty seats») while this type forbade it. Nothing reads the
   * union exhaustively — the only tests are `=== "starting"` and `=== "teamSelect"`, both correctly
   * false for a dealt table — so widening it is a type correction with no behavioural change. */
  readonly phase: "waiting" | "starting" | "teamSelect" | "playing";
  readonly players: readonly LobbyPlayer[];
  readonly seats: number;
  /**
   * The fill countdown FROZEN at this many seconds, because the public table dropped back to a
   * single human. Mutually exclusive with {@link countdown}: a paused countdown has no timer.
   * Absent on a table that never started one (nobody has joined you yet) and on every private
   * table. Optional/additive — a server that predates it simply never sends it.
   */
  readonly countdownPaused?: number;
  /** Seconds left on the "starting" countdown (only while `phase === "starting"`). */
  readonly countdown?: number;
  /** Epoch-ms deadline at which an un-started private table auto-closes (the host's idle window).
   *  The waiting room ticks a HOST-ONLY countdown to it; absent once the game deals. */
  readonly idleDeadline?: number;
  /** Table rules — private tables echo the host's chosen settings. */
  readonly settings?: TableSettings;
  /** True for a host-created friend table (vs. the public quick-match pool). */
  readonly isPrivate?: boolean;
  /** The Colyseus room id — used to build the invite deep-link. */
  readonly roomId?: string;
  /**
   * Did every seat hold a HUMAN when this table dealt? (2026-08-29.)
   *
   * A table that dealt short-handed never gave anybody a full game to abandon, so leaving it costs
   * nothing — the confirm dialog reads this to promise the right thing. ⚠️ OPTIONAL: an older
   * game-server sends nothing, and `undefined` must NOT read as «short-handed» or the dialog
   * starts offering free exits the server will still charge for. Test `=== false`.
   */
  readonly fullAtDeal?: boolean;
  /** Is this a PARTNERSHIP table (حکم/شلم — seat parity is the team)? A `define()`-time fact about
   *  the room, published so the client can ask «are all of my OPPONENTS bots» the way the server
   *  does, instead of keeping its own copy of the partnership-game list. Optional/additive. */
  readonly teams?: boolean;
  /**
   * RESUMED TABLE, CHAIR STILL EMPTY — the seats a persisted game names that nobody is sitting in
   * (2026-09-04). **Omitted, never `[]`**, so an older game-server reads as «none», which is what
   * every table it can produce actually is.
   *
   * ⚠️ These chairs are deliberately NOT in {@link LobbyState.players}: adding them would flip the
   * exit dialog's «are all my opponents bots?» from vacuously TRUE to false and silently re-charge
   * an exit the server makes free. They are folded in explicitly instead — see `OnlineGame`.
   *
   * ⚠️ The server derives it from seat OCCUPANCY, which a BOT satisfies, so it empties itself on
   * the same broadcast that seats the 10-minute resume fill. That is what lets the seat chip name
   * the missing player while the chair is empty and say «ربات» once one is really there.
   */
  readonly absentSeats?: readonly AbsentSeat[];
}

/**
 * Whose turn it is + the wall-clock (epoch ms) at which the turn timer expires.
 *
 * `totalMs` is how LONG that window is, so the ring can draw itself as a fraction of the window the
 * server actually armed. Optional only for version skew — a bundle newer than the game-server it is
 * talking to falls back to the configured length, exactly as it behaved before the field existed.
 */
export interface TurnInfo {
  readonly seat: number;
  readonly endsAt: number;
  readonly totalMs?: number;
}

/**
 * CHESS ONLY — the server-authoritative time banks for a TIMED chess table. `turnStartedAt`
 * (server epoch ms) + `turn` (0 = White / 1 = Black) let the client tick live remaining for the
 * side to move as `bank − (Date.now() − turnStartedAt)`; the SERVER decides flag-fall. Untimed
 * chess (and every card game) never sends this frame.
 */
export interface ClockInfo {
  readonly whiteMs: number;
  readonly blackMs: number;
  readonly turnStartedAt: number;
  readonly turn: number;
}

/** A player who dropped mid-game (pause policy) — drives the paused overlay. */
export interface PausedInfo {
  readonly seat: number;
  readonly name: string;
  readonly reason: string;
}

/**
 * CHESS ONLY — the opponent dropped mid-game and the reconnect grace is running. `seat` is the
 * GONE seat; `endsAt` (server epoch ms) is the grace deadline. Drives the present player's
 * "opponent reconnecting… mm:ss" overlay. Cleared on the opponent's return (a `resumed`) or
 * replaced by the policy outcome (`claimable`/`paused`/`left`) once the grace expires. Card games
 * never send this frame.
 */
export interface ReconnectingInfo {
  readonly seat: number;
  readonly endsAt: number;
}

/**
 * CHESS ONLY — a claimable table: `seat` is the PRESENT player who may CLAIM a win/draw while
 * their opponent is gone (disconnect policy `claim`, or `pause` once MAX_PAUSE elapses). The gone
 * player can still reconnect (which clears this); claiming is optional.
 */
export interface ClaimableInfo {
  readonly seat: number;
}

/** A player who left the table — drives the transient notice so a departure is never
 *  silent. Sent whenever a seat is lost: mid-game (a bot takes the chair) and when the
 *  HOST walks out of the waiting room (no bot, the seat simply frees). */
export interface LeftInfo {
  readonly seat: number;
  readonly name: string;
  readonly reason: string;
  /** They owned the table: the crown has moved (or the table is now ownerless), and
   *  the notice must say so — nothing the host used to control may stay blocked. */
  readonly wasHost?: boolean;
  /** A bot took their chair and play continues (mid-game). False pre-start: the seat
   *  is simply free again, and claiming a robot sat down would be a lie. */
  readonly botTook?: boolean;
}

/**
 * Handlers an online room drives, parameterised by the game's move (`M`) and
 * redacted view (`V`) types so the SAME protocol serves every multi-human game
 * (`hearts`, `hokm`, `shelem`, `pasur`, `bluff`). `onSeat` fires once on join
 * (with `isHost` for private tables); `onLobby` streams the waiting room;
 * `onTurn`/`onPaused`/`onResumed` drive the in-game lifecycle UI; `onState`
 * streams the redacted view (+ legal moves); `onGameOver` delivers final scores.
 */
export interface OnlineHandlers<M, V> {
  onSeat: (seat: number, isHost?: boolean) => void;
  onLobby: (lobby: LobbyState) => void;
  onState: (view: V, legal: readonly M[]) => void;
  onGameOver: (result: GameResult) => void;
  onTurn: (turn: TurnInfo) => void;
  /** The current turn ended with no human on turn (bots playing, or paused/ended):
   *  clear the countdown so it never freezes at 0 on a departed seat's deadline. */
  onTurnEnd: () => void;
  /** CHESS ONLY — the server pushed fresh clock banks (a move, the deal, or a reconnect re-sync).
   *  Optional so fakes/tests + the card games (which never send it) stay valid. */
  onClock?: (clock: ClockInfo) => void;
  onPaused: (paused: PausedInfo) => void;
  onResumed: () => void;
  onLeft: (info: LeftInfo) => void;
  /** CHESS ONLY — the opponent dropped; the reconnect grace is counting down (spec §9). Optional so
   *  fakes/tests + the card games (which never send it) stay valid. */
  onReconnecting?: (info: ReconnectingInfo) => void;
  /** CHESS ONLY — the present player may now CLAIM the win/draw (their opponent stayed gone past the
   *  grace, or the max-pause elapsed). Optional (card games never send it). */
  onClaimable?: (info: ClaimableInfo) => void;
  /** The HOST closed the waiting-room table: it is over before it began. Everyone
   *  still sitting there is told who closed it and returned to the lobby. Optional so
   *  fakes/tests predating it stay valid. */
  onTableClosed?: (by: string) => void;
  /** The waiting-room table was never started within the idle window → auto-closed. Everyone
   *  seated is told and returned to the lobby (distinct from a host-initiated close). Optional. */
  onTableExpired?: () => void;
  /** Another player reclaimed their bot-held seat (online re-join); optional (roster already shows it). */
  onRejoined?: () => void;
  /** THE HOST REMOVED US from their private table («اخراج»). Addressed to us, and final: every
   *  door back is already shut server-side. Optional so fakes/tests predating it stay valid. */
  onKicked?: () => void;
  /**
   * card-86 r3 item 2 — the game-server closed `roomId` with the consented code after our `leave()`:
   * it heard the «خروج» and ran its leave (the seat freed; a host's waiting room closed). Never on
   * any other close, and never when no close arrives (a dead socket). Optional so fakes stay valid.
   */
  onLeaveHeard?: (roomId: string) => void;
  /** A NEWER COPY OF THE APP TOOK THIS SEAT BACK (card-74): the server let this socket go with
   *  `SEAT_TAKEN_OVER_CLOSE_CODE`. Final — the connection is released, nothing reconnects, and the
   *  seat and its saved ticket now belong to the other copy. Optional so fakes predating it stay valid. */
  onTakenOver?: () => void;
  /** «بازی دوباره» — the private HOST has opened a successor table and everyone is invited into
   *  it. Broadcast to the whole room, the host included, so one frame drives every screen.
   *  Optional so fakes/tests predating it stay valid. */
  onRematchOffered?: (offer: { roomId: string; by: number }) => void;
  /** The server REFUSED the host's «شروع» or «بازی دوباره» — today only during maintenance, which
   *  stops new games — and says why (`message`, shown as is). A refused start used to be silent.
   *  Optional so fakes/tests predating it stay valid. */
  onStartRefused?: (message: string) => void;
  /** CONTRACT C1 — the game-server's verdict on this PRIVATE table's rematch, after the terminal
   *  and on every post-match presence change. Shape-checked before it gets here. An OLD
   *  game-server never sends it (the rematch decision reads that as UNKNOWN). Optional. */
  onRematchState?: (state: RematchStateFrame) => void;
  /**
   * THE FINISHED ROOM'S SOCKET CLOSED UNDER US — after `gameover`, and not by our own hand.
   *
   * ⚠️ `gameover` RELEASES the managed connection (a finished match must never auto-rejoin), and
   * from then on a close reached no handler at all — so a phone lock, a network blip or EVERY
   * game-server deploy left the owner's «بازی دوباره» live over a dead socket (it did nothing) and a
   * guest waiting for an offer that could never arrive (`02-rematch.md` R5/R7/R12). Optional.
   */
  onRoomGone?: () => void;
  /** FEATURE 3 — the server minted a reconnection token for a friend the host invited into a bot
   *  seat mid-game. The host's client then creates the friend-invite carrying `token`/`seat`. */
  onSeatTokenMinted?: (payload: {
    seat: number;
    toUserId: string;
    roomId: string;
    token: string;
    game?: string;
  }) => void;
  onError: (message: string) => void;
  /** The connection was re-established after a drop (same seat, fresh socket):
   *  reset transient turn/pause state — the server re-emits the truth right
   *  after. Optional so fakes/tests predating reconnection stay valid. */
  onReset?: () => void;
}

export interface OnlineHandle<M> {
  send: (move: M) => void;
  /** Host-only: push updated table settings while in the lobby. */
  sendSettings?: (settings: TableSettings) => void;
  /** Host-only: start the private table. The {@link StartIntent} tells the server
   * WHICH start was pressed — «با ربات‌ها شروع کن» ⇒ `{withBots:true}` (deal now,
   * never stage teams); a full-human start sends nothing. */
  start?: (intent?: StartIntent) => void;
  /** Host-only, during `teamSelect`: apply a seat arrangement —
   * `order[newSeat]` = the CURRENT seat of the player moving to `newSeat`
   * (server-validated: host + phase + permutation of all-human seats). */
  setTeams?: (order: readonly number[]) => void;
  /** Host-only: resume a paused game by filling the dropped seat with a bot. */
  resume?: () => void;
  /** CHESS ONLY — the present player claims the win/draw while their opponent is gone (a claimable
   *  chess table). Optional so fakes/tests + the card games stay valid. */
  claim?: () => void;
  leave: () => void;
  /**
   * Close the way a DROPPED socket does — non-consented, so the server holds the seat for its grace,
   * a bot takes it after, and nothing is charged. For a join that landed after the table screen was
   * left, and for a table the app CRASHED out from under (card-a5 item 3): the player pressed nothing
   * that says «leave», and a CONSENTED close is what the server reads as one (`leaveByClose` — a bot
   * at once, and a claimable public شطرنج/تخته نرد).
   */
  drop: () => void;
  /**
   * «خروج بدون بازگشت» — leave PERMANENTLY: tell the server this exit is final (no reclaimable
   * seat held), WAIT briefly for its verdict, then disconnect. Resolves with what the leave cost
   * ({@link LeavePenaltyInfo}), or `null` if the server never answered — the exit still happens.
   * Optional so fakes/tests predating it stay valid.
   */
  leaveForever?: () => Promise<LeavePenaltyInfo | null>;
  /**
   * CARD-98 ITEM 3a — the close about to follow comes from the WAITING-ROOM screen: the cards were
   * not on this phone. Sent just ahead of `leave()` (one socket, so it arrives first); the server
   * takes it only within 3 s of a deal, from a seat that has not played since. Optional so fakes stay
   * valid.
   */
  leaveWaiting?: () => void;
  /** Host-only (feature 3): ask the server to mint a reconnection token for a friend at a specific
   *  BOT seat mid-game; the reply arrives via {@link OnlineHandlers.onSeatTokenMinted}. Optional. */
  mintSeatToken?: (seat: number, toUserId: string) => void;
  /** Host-only: remove the player at `seat` from this private table («اخراج»). The server
   *  re-checks that we are the host, so a crafted client gains nothing by calling it. */
  kick?: (seat: number) => void;
  /** Host-only: ask the server to open the rematch successor for this finished private table.
   *  Host / private / terminal are all re-checked there, so this is an affordance, not a right. */
  requestRematch?: () => void;
}

/**
 * WHAT A PUBLIC-TABLE QUIT COST (2026-08-27) — the server's reply to «خروج بدون بازگشت», sent
 * BEFORE the socket closes so the notice can name the number.
 *
 * `recordedLoss: false` means the rule did not apply at all (a private table, a game not in
 * progress, the win screen) and NOTHING was recorded — the ordinary silent exit.
 */
export interface LeavePenaltyInfo {
  readonly recordedLoss: boolean;
  /** How many public games they have quit inside the running 24h window (0 ⇒ not counted). */
  readonly leavesInWindow: number;
  /** Present ONLY when this leave just triggered a lockout: how long it lasts, in ms. */
  readonly lockoutMs?: number;
}

/** How long to wait for that reply before leaving anyway — an exit must never hang. */
export const LEAVE_ACK_TIMEOUT_MS = 2000;

/**
 * How long a «خروج» confirmed while the socket was DOWN waits for the lifecycle's reconnect to bring it
 * back before giving up (card-a5 item 1). The frame means nothing on a dead socket — `safeSend` would
 * swallow it and the player would believe they had left while the server still held their seat. Long
 * enough for a reconnect over a network that has just come back; short enough that a player who chose
 * to leave is not kept at a table that cannot be reached.
 */
export const LEAVE_LIVE_WAIT_MS = 8_000;

/**
 * «خروج» COULD NOT BE DELIVERED — the socket never came back inside {@link LEAVE_LIVE_WAIT_MS}
 * (card-a5 item 1). Nothing reached the server, so nothing is charged and the seat is still held: the
 * caller keeps this device's ticket (the way back) and goes where the player asked. A failed
 * reconnect is free — the operator's «a genuine connection loss stays free».
 */
export class LeaveNotDeliveredError extends Error {
  constructor() {
    super("online: «خروج» was not delivered — the socket never came back");
    this.name = "LeaveNotDeliveredError";
  }
}

/**
 * How long a «ادامه بازی» return waits for the table's recorded fate before carrying on with its
 * ordinary patience — a status lookup that never answers must not hold the rejoin hostage.
 */
export const TABLE_STATUS_LOOKUP_TIMEOUT_MS = 3000;

/** The fates after which no ticket opens the table again. */
const TABLE_OVER: ReadonlySet<TableStatusResponse["state"]> = new Set([
  "finished",
  "closed",
  "expired",
]);

/**
 * A «ادامه بازی» RETURN TO A TABLE THAT IS OVER (session B, item 3 — 2026-10-08).
 *
 * The claim's terminal rejects every reservation, so for as long as the room lives Colyseus answers
 * the leaver's ticket `4214` — which the rejoin's patience read as «not yet» for twelve seconds
 * before saying «فرصت برگشتن به این میز تمام شد» (explore `03-exits.md` §3a). The first `4214` now
 * asks the table's recorded fate, and an over table ends the rejoin AT ONCE with this, carrying the
 * status — `end` included, when the server recorded how — so the screen can say what happened.
 */
export class TableOverError extends Error {
  /** What the status route answered (`state` is finished / closed / expired). */
  readonly status: TableStatusResponse;
  /** The refusal that prompted the question (the `4214`). */
  readonly refusal: unknown;
  constructor(status: TableStatusResponse, refusal: unknown) {
    super(`online: the table is ${status.state}`);
    this.name = "TableOverError";
    this.status = status;
    this.refusal = refusal;
  }
}

/**
 * Connects to an online room and wires the handlers. Abstracted so the store is
 * testable with a fake connector (no WebSocket) — mirrors `GameConnector`.
 */
export type OnlineConnector<M, V> = (
  handlers: OnlineHandlers<M, V>,
  /** Aborted when the table is left before the join has landed (the store's `disconnect`). A
   *  connector that keeps trying — the patient «ادامه بازی» rejoin — stops asking when it is. */
  signal?: AbortSignal,
) => Promise<OnlineHandle<M>>;

interface StateMessage<M, V> {
  view: V;
  legal: M[];
}

/**
 * The real connector. Acquires the room three ways, chosen by `join`:
 * - `quick`  — `joinOrCreate("<game>-online")` (public quick-match, the default),
 * - `create` — `create("<game>-private", { settings })` (host makes a friend table),
 * - `join`   — `joinById(roomId)` (a friend joins that table from an invite).
 *
 * All games speak the same protocol, so only the room name (`room`, and the
 * derived `privateRoom`) varies per game.
 */
export function createOnlineConnector<M, V>(opts: {
  wsUrl: string;
  /** Quick-match room name, e.g. `"hearts-online"`. */
  room: string;
  /** Private (host-created) room name; defaults to `room` with `-online`→`-private`. */
  privateRoom?: string;
  getInitData: () => string | null;
  /** How to acquire the room: quick-match (default), host-create, or join-by-id. */
  join?: OnlineJoin;
  /** Chosen bot-fill strength for tables THIS client creates (quick-match or a
   *  host's private table). Ignored when joining someone else's table by id — its
   *  creator already fixed the difficulty. Validated + defaulted server-side. */
  difficulty?: BotDifficulty;
  /** Called with the live seat's roomId + reconnection token on the first connect AND after every
   *  reconnect (the token rotates). The caller persists it for same-device re-join. */
  onLive?: (info: { roomId: string; token: string }) => void;
  /** RESUMABLE ONLINE GAMES (chess) — the stable `OnlineGame` row this table persists to.
   *  The same game is ALSO offered from the DB on the «ادامه بازی» strip, so the caller
   *  stores this alongside the re-join token and the two entries can be recognised as ONE
   *  game instead of appearing twice. Never sent by a card table. */
  onSavedGame?: (onlineGameId: string) => void;
  /** A `rejoin` whose saved token was refused (match ended / room gone / seat not reclaimable):
   *  the caller drops the stale saved game so the «ادامه بازی» strip stops offering it. */
  onRejoinFailed?: (error: unknown) => void;
  /**
   * THIS DEVICE'S RE-JOIN TICKET FOR `roomId`, IF IT HOLDS ONE — the key to a table whose front
   * door is locked.
   *
   * Consulted on ONE path only: a `join` (an invite link / an accepted invite) refused because the
   * room is LOCKED. A player who is already seated at that table and whose app still has the
   * stashed token was being told «بازی این میز شروع شده است» while holding the thing that gets
   * them back in (prod, 2026-09-02). Returns null for any other room, or when nothing is saved.
   */
  ticketFor?: (roomId: string) => string | null;
  /**
   * THE KEY THE INVITE CAME WITH, FOR `roomId` (card-c1) — the seat-fill token of an invite accepted on
   * the way in. Tried at the same door, after {@link ticketFor}'s own, and only there: it is ONE-SHOT,
   * and `ticketFor` is also read on every try of a «ادامه بازی» return, which would spend it unused.
   */
  seatFillTicketFor?: (roomId: string) => string | null;
  /**
   * `GET /tables/:roomId/status` — asked by a `rejoin` on its FIRST «not yet» (`4214`), so a table
   * that is OVER ends the wait at once ({@link TableOverError}) instead of after twelve seconds.
   * Omitted (every other door, older callers, the tests that predate it) ⇒ today's patience.
   */
  tableStatus?: (roomId: string) => Promise<TableStatusResponse>;
  /** Lifecycle registry (injected in tests; defaults to the app singleton). */
  registry?: ConnectionRegistry;
}): OnlineConnector<M, V> {
  return async (handlers, signal) => {
    const initData = opts.getInitData() ?? "";
    // Every reconnect below — the drop's reacquire, «ادامه بازی», the locked-invite ticket — asks
    // for a connect ticket, so the seat's token never rides a socket URL (card-a5 item 2), and carries
    // this launch's initData, so the game-server orders the socket that comes back by THIS copy's
    // launch, not the one it inherits from the seat's previous socket (card-74 round 2, #1).
    const client = withConnectTickets(new Client(opts.wsUrl), initData);
    const join = opts.join ?? QUICK_MATCH;
    const privateRoom = opts.privateRoom ?? opts.room.replace(/-online$/, "-private");
    const withDifficulty = opts.difficulty ? { difficulty: opts.difficulty } : {};

    /**
     * THE «ادامه بازی» DOOR HAS THE RECONNECT'S RACE — AND IT USED TO LOSE THE TICKET TO IT.
     *
     * A relaunch inside the server's detection window (the old WebView died without a close
     * frame and the player is back within 3–9 s) asks for a reservation `onLeave` has not minted
     * yet, and is refused `4214`. That refusal ran `onRejoinFailed` — the saved ticket DELETED,
     * so the strip stopped offering the game — and said «فرصت برگشتن به این میز تمام شد», while
     * the server was about to hold the seat for its full grace. Same rule as the reacquire below:
     * a 4214 is «not yet» for {@link RECONNECT_TOO_EARLY_WINDOW_MS} from the FIRST refusal,
     * retried every {@link RECONNECT_TOO_EARLY_RETRY_MS}, with the SAME saved ticket; past the
     * window it is the answer, and everything after that is exactly as before. Any other failure
     * ends the attempt at once. The player leaving (`signal`) ends it too.
     *
     * ⚠️ AND A RETRY ALSO ASKS WITH THE TICKET THE STRIP HOLDS NOW, WHEN THAT IS A DIFFERENT KEY.
     * The table this player left can still have a reconnect in flight (the overlay's «خروج» was
     * tapped mid-attempt). When it lands, the server hands the seat to it, the app closes it the
     * way a drop closes, and the reservation is re-minted under the ROTATED token — which reaches
     * the saved ticket (`refreshOnlineGameToken`) but not the token this rejoin was handed, so
     * every retry asked with a spent key until the window closed. Alternate the two: the first
     * try is always the token we were handed (an invite's seat-fill token is fresher than any
     * ticket this device kept for the room), and a ticket that never changed adds nothing. And when
     * BOTH reconnects were granted the same reservation, the one that loses the upgrade is told
     * «seat reservation expired.» — patient too ({@link isReservationLost}), or that refusal deleted
     * the ticket the write-through had just made good (staging: tapped as the old one landed).
     */
    const rejoinPatiently = async (
      roomId: string,
      token: string,
    ): Promise<Awaited<ReturnType<typeof client.reconnect>>> => {
      let firstRefusal: number | null = null;
      let askedTheTable = false;
      for (let attempt = 0; ; attempt++) {
        const saved = opts.ticketFor?.(roomId) ?? null;
        const key = saved !== null && attempt % 2 === 1 ? saved : token;
        try {
          return await client.reconnect(key);
        } catch (error) {
          if (!(isTooEarly(error) || isReservationLost(error)) || signal?.aborted) throw error;
          const now = Date.now();
          firstRefusal ??= now;
          if (now - firstRefusal >= RECONNECT_TOO_EARLY_WINDOW_MS) throw error;
          /*
           * …BUT A TABLE THAT IS OVER IS NOT «NOT YET» (session B, item 3). The same 4214 is what a
           * finished table answers for as long as its room lives — the claim's terminal rejected
           * every reservation — and twelve seconds of patience then said «فرصت برگشتن…» about a game
           * that had ended. So the FIRST 4214 asks the table's recorded fate, once: over ends the
           * wait now, carrying how; anything else (still playing, no record, a lookup that fails or
           * hangs) is the patience above, unchanged. A lost upgrade (`isReservationLost`) is a live
           * table by definition and is never asked about.
           */
          if (isTooEarly(error) && !askedTheTable) {
            askedTheTable = true;
            const over = await tableIsOver(roomId);
            if (over) throw new TableOverError(over, error);
            if (signal?.aborted) throw new Error(`online:${opts.room}: rejoin abandoned`);
            /*
             * …AND A 4214 IS ALSO WHAT AN OLDER COPY OF THIS APP STILL HOLDING THE SEAT PRODUCES
             * (card-74): its socket is alive, so no reservation exists for any token. Ask for the
             * seat back, ONCE — the game-server's `takeover` door, carrying this launch's initData,
             * which it grants only to a player who holds a chair at that table and is no older a
             * launch than the copy it lets go. Refused (any reason, or an older server that has no
             * such door) → the patience below, exactly as before.
             */
            const taken = await takeSeatBack(client, roomId, initData, "return");
            // …a NEWER copy holds it (round 2, #3): no patience — the answer, at once.
            if (taken === SEAT_MOVED) throw new SeatMovedError();
            if (taken !== null) return taken;
            if (signal?.aborted) throw new Error(`online:${opts.room}: rejoin abandoned`);
          }
          await new Promise<void>((resolve) => setTimeout(resolve, RECONNECT_TOO_EARLY_RETRY_MS));
          if (signal?.aborted) throw new Error(`online:${opts.room}: rejoin abandoned`);
        }
      }
    };

    /** The table's recorded fate when it is OVER, else null — never throws, never hangs past
     *  {@link TABLE_STATUS_LOOKUP_TIMEOUT_MS}. No lookup wired (older callers) ⇒ null. */
    const tableIsOver = async (roomId: string): Promise<TableStatusResponse | null> => {
      const lookup = opts.tableStatus;
      if (!lookup) return null;
      let timer: ReturnType<typeof setTimeout> | undefined;
      try {
        const status = await Promise.race([
          lookup(roomId),
          new Promise<null>((resolve) => {
            timer = setTimeout(() => resolve(null), TABLE_STATUS_LOOKUP_TIMEOUT_MS);
          }),
        ]);
        return status !== null && TABLE_OVER.has(status.state) ? status : null;
      } catch {
        return null; // a failed lookup is no answer — the patience stands
      } finally {
        if (timer !== undefined) clearTimeout(timer);
      }
    };

    /**
     * BACK TO THE HELD SEAT WITH NO KEY ON THIS DEVICE (card-9f) — a `rejoin` whose token is `null`: the
     * game-server named the table itself (the «بازی عمومی» refusal's `askHeldSeat`), so there is nothing
     * to reconnect with. Its held-seat door (`takeover`, origin `held`) hands the seat back to THIS
     * launch's validated player only — a human chair through card-74's reservation, a bot-held one as
     * its own reservation by a connect ticket — and the answer is consumed unchanged.
     *  · a newer copy holds the seat → the takeover code, at once (`SeatMovedError`);
     *  · the upgrade LOST the race (the duplicated reconnect's loser — or the grace ran out on its
     *    way, and the next ask gets the bot-held branch) → asked ONCE more, after the first attempt
     *    settled (never two of this page's door joins at once: the door lets every other socket of
     *    the player go);
     *  · refused → the table's fate when it is over (`TableOverError`), else colyseus's own 4214
     *    sentence, which `join-error` reads as «فرصت برگشتن به این میز تمام شد».
     */
    const returnThroughDoor = async (
      roomId: string,
    ): Promise<Awaited<ReturnType<typeof client.reconnect>>> => {
      for (let attempt = 0; attempt < 2; attempt++) {
        const taken = await takeSeatBack(client, roomId, initData, "held", { surfaceLost: true });
        if (taken === SEAT_MOVED) throw new SeatMovedError();
        if (taken === SEAT_LOST) {
          if (signal?.aborted) break;
          continue;
        }
        if (taken !== null) return taken;
        break;
      }
      if (signal?.aborted) throw new Error(`online:${opts.room}: rejoin abandoned`);
      const refusal = new MatchMakeError("reconnection token invalid or expired.", 4214);
      const over = await tableIsOver(roomId);
      if (over) throw new TableOverError(over, refusal);
      throw refusal;
    };

    /**
     * The five join paths as ONE promise, so {@link watchJoin} can watch the WHOLE attempt —
     * the HTTP matchmake AND the WebSocket upgrade it hands off to. The upgrade is the leg that
     * fails: on 2026-08-22 a player's reservations were minted on time and her upgrades arrived
     * past the 15s TTL, or never, and nothing on our side said so. The branches below are
     * unchanged; only the `await` moved out.
     */
    const openRoom = async (): Promise<Awaited<ReturnType<typeof client.joinOrCreate>>> =>
      join.kind === "create"
        ? await client.create(privateRoom, {
            initData,
            settings: join.settings,
            ...withDifficulty,
          })
        : join.kind === "join"
          ? await client.joinById(join.roomId, {
              initData,
              // The invited seat, when the accepted invite named one. A PREFERENCE: the room
              // seats them there only if that chair is still free (TableController.join).
              ...(join.preferredSeat !== undefined ? { preferredSeat: join.preferredSeat } : {}),
              // A LINK join (`t_<game>_<roomId>` — the Telegram PV button, a copied link) has no
              // seat in the URL. Telling the server so lets it read the invited seat off the
              // invite itself. Quick-match never sets this, so its join path is untouched.
              ...(join.fromLink ? { fromLink: true } : {}),
            })
          : join.kind === "rejoin"
            ? join.token === null
              ? // …WITH NO KEY ON THIS DEVICE (card-9f): the game-server's held-seat door.
                await returnThroughDoor(join.roomId)
              : // RE-JOIN a live game: reconnect with the saved token (a locked room refuses joinById),
                // patiently — a 4214 may only mean the server has not noticed the drop yet.
                await rejoinPatiently(join.roomId, join.token)
            : join.kind === "resume"
              ? // RESUME a persisted online chess game: joinOrCreate with the stable onlineGameId as
                // the filterBy value — the FIRST resumer creates + rehydrates the room from the DB,
                // the SECOND matches the same room. (Difficulty is irrelevant — no bots to fill.)
                await client.joinOrCreate(opts.room, {
                  initData,
                  onlineGameId: join.onlineGameId,
                })
              : // FRESH public quick-match. The `onlineGameId: ""` is LOAD-BEARING, not noise.
                //
                // `chess-online` / `backgammon-online` are registered with
                // `filterBy(["onlineGameId"])`, and Colyseus's `getFilterOptions` only adds a
                // query condition when the client's options `hasOwnProperty` the field. Omitting
                // it therefore adds NO condition — which does not mean "match only fresh rooms",
                // it means "match ANY room", including a RESUME room sitting unlocked while it
                // waits for its second player. The quick-matcher was then seated into someone
                // else's persisted game and refused at onAuth with «تو در این بازی نیستی» — 166
                // occurrences on 2026-07-31 alone, 4–38/hour, a mode-level outage.
                //
                // The empty string gives the query a real condition: fresh rooms list `""` and
                // pair with each other, resume rooms list their uuid and are excluded. It cannot
                // turn a fresh join INTO a resume, because `isResume` requires `length > 0`.
                // Harmless for the card rooms, which declare no filter at all.
                await client.joinOrCreate(opts.room, {
                  initData,
                  onlineGameId: "",
                  ...withDifficulty,
                });

    /**
     * THE SECOND DOOR — tried only after the first is refused, and only when we hold its key.
     *
     * An invite link is `joinById`, which a table that has DEALT refuses because the room is
     * locked. That refusal is correct for a stranger and wrong for exactly one person: somebody
     * already SEATED there whose app still holds this device's re-join ticket. `client.reconnect`
     * skips the lock (it is what «ادامه بازی» does), so declining to try it was the app refusing a
     * player at a door it had the key to — reported from prod 2026-09-02 by somebody the server
     * had ALREADY put back in her chair off that very token.
     *
     * Narrow on purpose: a `join` only, a LOCKED refusal only ({@link isRoomLocked} — a disposed
     * room cannot be reopened by anything), and the ticket must name THIS room. When the ticket is
     * stale too, the ORIGINAL refusal is what propagates, so the player still reads the true
     * reason ({@link tableJoinReason}) rather than a Colyseus token complaint.
     *
     * …AND THE SAME DOOR OPENS TO THE KEY A SEAT-FILL INVITE CAME WITH (card-c1): a friend the host
     * invited into a bot chair is refused the same way, and has no ticket of their own.
     */
    const ticketFallback = async (
      error: unknown,
    ): Promise<Awaited<ReturnType<typeof client.joinOrCreate>> | null> => {
      if (join.kind !== "join" || !isRoomLocked(error)) return null;
      for (const ticketOf of [opts.ticketFor, opts.seatFillTicketFor]) {
        if (signal?.aborted) return null; // nobody is here: a one-shot key is not spent for them
        const token = ticketOf?.(join.roomId) ?? null;
        if (!token) continue;
        try {
          return await watchJoin(
            { conn: `online:${opts.room}`, kind: "rejoin" },
            client.reconnect(token),
          );
        } catch {
          // stale as well — the original refusal stays the honest answer
        }
      }
      return null;
    };

    let room: Awaited<ReturnType<typeof client.joinOrCreate>>;
    try {
      room = await watchJoin({ conn: `online:${opts.room}`, kind: join.kind }, openRoom());
    } catch (error) {
      const recovered = await ticketFallback(error);
      if (recovered === null) {
        // The saved token is stale — drop it. NOT when the player walked away mid-attempt: nothing
        // refused the ticket then, and the strip should still offer it. Nor when a NEWER copy of this
        // player holds the seat (round 2, #3): the ticket on this phone is that copy's.
        if (join.kind === "rejoin" && !signal?.aborted && !isSeatMoved(error)) {
          opts.onRejoinFailed?.(error);
        }
        throw error;
      }
      room = recovered;
    }
    opts.onLive?.({ roomId: room.roomId, token: room.reconnectionToken });

    // Whether the table ever reached "playing" on this connection — a state
    // frame is playing-only too, so both mark it.
    let gameStarted = false;
    /** OUR OWN seat, tracked here so the `left` handler below can tell "somebody left" from
     *  "I was removed" without reaching into the store. Null until the room seats us. */
    let mySeat: number | null = null;
    /** Resolver for the in-flight «خروج بدون بازگشت» verdict (null when none is pending). */
    let leaveAck: ((info: LeavePenaltyInfo | null) => void) | null = null;
    /** The match is over on this connection (`gameover` landed and released it). */
    let finished = false;
    /** WE closed it — «خروج», «خروج بدون بازگشت», or a drop of a join nobody holds. Our own close
     *  comes back through the same `onLeave`, and it is not «the room is gone». */
    let closedByUs = false;
    /** card-86 r3 item 2 — `leave()` sent the server our consented close (see `onLeaveHeard`). */
    let leftConsented = false;
    /** A newer copy of the app took this seat (card-74): this connection is over, for good. */
    let takenOver = false;
    /**
     * THE TAKEOVER, ON THE SOCKET IT NAMES — final when that socket is this connection's CURRENT one.
     * One the page has already moved past (a socket torn down for a reconnect whose listeners outlived
     * it) is ignored: it says nothing about the seat this page holds now.
     */
    const takenOverOn = (r: unknown): void => {
      if (takenOver || closedByUs || managed.current() !== r) return; // once: the ERROR, then its close
      takenOver = true;
      managed.release();
      handlers.onTakenOver?.();
    };
    /**
     * card-74 review #1b — THE TAKEOVER DOOR FROM THE PAGE'S OWN RECONNECT, per OUTAGE (keyed by the room
     * that died, which every attempt of that outage is handed). Asked once, on the first 4214, and never
     * beside another reconnect of this page that is still out or already came back: the door lets go
     * every other socket of the player, and the page's own racing reconnect (CLAUDE.md's standing
     * verdict: one drop can send two) must never be one of them. While it is out, a new attempt waits
     * for its answer instead of sending a reconnect it could race.
     */
    const outages = new WeakMap<
      object,
      {
        inFlight: number;
        recovered: boolean;
        door: Promise<Room | typeof SEAT_MOVED | null> | null;
        /** The one extra ask before «seat expired» was made (round 3, #1). */
        lastChance: boolean;
      }
    >();
    /**
     * …AND THE DOOR'S «A NEWER COPY HOLDS IT» (card-74 round 2, #3): this page's socket was half-open at
     * the takeover and never got the frames, so this is how it learns — taken over, on the room that
     * died (still the connection's current one while it is out), final. The error ends the attempt; the
     * connection is released, so nothing reports it.
     */
    const seatMoved = (previous: object): Error => {
      takenOverOn(previous);
      return new SeatMovedError();
    };
    const outageOf = (r: object) => {
      let outage = outages.get(r);
      if (!outage) {
        outage = { inFlight: 0, recovered: false, door: null, lastChance: false };
        outages.set(r, outage);
      }
      return outage;
    };
    /** When THIS outage's first «not yet» refusal came — null while the connection is up. The
     *  window is wall clock on purpose: the server's detection runs whether or not the phone is
     *  asleep, unlike the retry budget. See {@link RECONNECT_TOO_EARLY_WINDOW_MS}. */
    let tooEarlySince: number | null = null;
    const managed = manageRoom({
      name: `online:${opts.room}`,
      registry: opts.registry,
      initial: room,
      // THIS DOOR'S RESERVATION IS `allowReconnection`'s, NOT THE FRESH-JOIN WINDOW — see
      // ONLINE_REACQUIRE_TIMEOUT_MS for what the number was measured against, and for why being
      // too short costs the seat rather than merely a retry.
      reacquireTimeoutMs: ONLINE_REACQUIRE_TIMEOUT_MS,
      // The reservation this door consumes exists only once the server has let go of the OLD
      // socket, and the close we are about to send is what makes it let go — so let it land first.
      closeWaitMs: ONLINE_CLOSE_WAIT_MS,
      // ONLINE reconnect reclaims the SAME seat: the server holds it open for a
      // reconnection window after a non-consented drop, and `client.reconnect`
      // consumes that reservation. Window expired / room gone → the seat is
      // unrecoverable → SeatExpiredError stops the retry cycle (the overlay
      // shows its "seat expired" state and the player exits to the lobby).
      reacquire: async (previous) => {
        const outage = outageOf(previous);
        if (outage.door !== null) {
          // The door is asked for this outage: its answer decides this attempt.
          const answer = await outage.door;
          if (answer === SEAT_MOVED) throw seatMoved(previous);
          if (answer !== null) {
            throw new Error(`online:${opts.room}: the seat came back through the takeover door`);
          }
        }
        outage.inFlight++;
        let out = true;
        const settle = (): void => {
          if (out) outage.inFlight--;
          out = false;
        };
        try {
          const next = await client.reconnect(previous.reconnectionToken);
          settle();
          outage.recovered = true;
          tooEarlySince = null; // this outage is over; the next one gets a window of its own
          opts.onLive?.({ roomId: next.roomId, token: next.reconnectionToken }); // token rotated
          return next;
        } catch (error) {
          settle();
          // …AN OLDER COPY OF THE APP MAY HOLD THE SEAT — one that walked in on this device's shared
          // token while this page was away: no reservation then exists for anybody, however long we
          // wait. Ask for it back through the door (review #1b), once, alone (see `outages`).
          if (
            isTooEarly(error) &&
            outage.door === null &&
            outage.inFlight === 0 &&
            !outage.recovered
          ) {
            outage.door = takeSeatBack(client, previous.roomId, initData, "reconnect");
            const taken = await outage.door;
            if (taken === SEAT_MOVED) throw seatMoved(previous);
            if (taken !== null) {
              tooEarlySince = null;
              opts.onLive?.({ roomId: taken.roomId, token: taken.reconnectionToken });
              return taken;
            }
          }
          // «NOT YET», NOT «GONE»: refused because the server has not registered the drop yet —
          // the seat is still ours. Retry, for as long as the server can take to notice.
          if (isTooEarly(error)) {
            const now = Date.now();
            tooEarlySince ??= now;
            if (now - tooEarlySince < RECONNECT_TOO_EARLY_WINDOW_MS) {
              throw new ReconnectTooEarlyError(messageOf(error), RECONNECT_TOO_EARLY_RETRY_MS);
            }
          }
          /*
           * …AND ONE LAST CHANCE BEFORE «جات به ربات سپرده شد» (card-74 round 3, #1). The ask above happens
           * once, and only alone: it can fail on the network, or never happen while another attempt of
           * this page is still out — and a copy a newer one superseded while its socket was half-open then
           * ended on that overlay. So before this 4214 becomes «seat expired», the door is asked ONCE more
           * per outage (never a loop): a newer copy holding the seat → taken over; the seat itself → back,
           * but taken only when nothing else of this page is out (the door lets every other socket of the
           * player go, that attempt's included, should it land); nothing → expired, exactly as before.
           * Later attempts of the outage wait for this answer, as for the first.
           */
          if (isTooEarly(error) && !outage.lastChance && !outage.recovered) {
            outage.lastChance = true;
            outage.door = takeSeatBack(client, previous.roomId, initData, "reconnect", {
              consume: outage.inFlight === 0,
            });
            const answer = await outage.door;
            if (answer === SEAT_MOVED) throw seatMoved(previous);
            if (answer !== null) {
              tooEarlySince = null;
              opts.onLive?.({ roomId: answer.roomId, token: answer.reconnectionToken });
              return answer;
            }
          }
          // Pre-start (waiting/teamSelect/starting) there is NO bot takeover —
          // the seat was merely freed. The flag picks the truthful overlay copy.
          if (isSeatGone(error)) throw new SeatExpiredError(messageOf(error), !gameStarted);
          throw error;
        }
      },
      onReconnected: () => handlers.onReset?.(),
      wire: (r) => {
        r.onMessage("saved", (msg: { id?: unknown }) => {
          if (typeof msg?.id === "string" && msg.id) opts.onSavedGame?.(msg.id);
        });
        r.onMessage("seat", (msg: { seat: number; isHost?: boolean }) => {
          mySeat = msg.seat;
          handlers.onSeat(msg.seat, msg.isHost);
        });
        r.onMessage("lobby", (lobby: LobbyState) => handlers.onLobby(lobby));
        /*
         * THE HOST REMOVED US. Release the managed connection FIRST, exactly as an idle removal
         * naming our own seat does two handlers down: the seat is already a bot's and every door
         * back is shut, so letting the lifecycle keep the socket alive would end in the
         * reconnect overlay's «جات به ربات سپرده شد» painted over our own notice — the second-
         * notice defect that path was fixed for.
         */
        r.onMessage("kicked", () => {
          // …except right after a takeover: then it is the game-server's carrier for OLDER bundles,
          // the frame they release the connection on (card-74, `letGo`), and nobody kicked anybody.
          if (takenOver) return;
          managed.release();
          handlers.onKicked?.();
        });
        // The rematch offer. Shape-checked at the edge like every other inbound frame: a crafted
        // server (or a future one that changes the payload) must not put `undefined` into the
        // store, where it would light the button up with nowhere to go.
        r.onMessage("rematch", (msg: { roomId?: unknown; by?: unknown }) => {
          if (typeof msg?.roomId !== "string" || msg.roomId.length === 0) return;
          handlers.onRematchOffered?.({
            roomId: msg.roomId,
            by: typeof msg.by === "number" ? msg.by : -1,
          });
        });
        // The host's start / rematch was refused (maintenance). Shape-checked like the frame above.
        r.onMessage("start-refused", (msg: { message?: unknown }) => {
          if (typeof msg?.message === "string" && msg.message.length > 0) {
            handlers.onStartRefused?.(msg.message);
          }
        });
        // CONTRACT C1 — the rematch verdict. A malformed one is dropped, never guessed at.
        r.onMessage("rematch-state", (msg: unknown) => {
          const state = readRematchState(msg);
          if (state) handlers.onRematchState?.(state);
        });
        /*
         * THE FINISHED ROOM'S SOCKET CLOSING UNDER US — see `OnlineHandlers.onRoomGone`.
         *
         * Its own listener, beside the lifecycle's: `release()` only stops the lifecycle from
         * reconnecting; the room still reports its close, and this is the one listener that wants
         * it. Before `gameover` a close is the lifecycle's drop (it reconnects), and a close we
         * caused ourselves is not news — both stay silent here.
         */
        r.onLeave((code: number) => {
          /*
           * A NEWER COPY OF THE APP HAS THIS SEAT NOW (card-74) — the server closed us with the one
           * code it uses for that. Release FIRST, as a kick does: this listener runs before the
           * lifecycle's own (registered after `wire`), so the drop is never reported and nothing
           * reconnects — a reconnect could not win it back anyway, and every retry would only be
           * refused. The saved ticket is the other copy's now; nothing here touches it.
           */
          if (code === SEAT_TAKEN_OVER_CLOSE_CODE) {
            takenOverOn(r);
            return;
          }
          /*
           * card-86 r3 item 2 — THE SERVER HEARD OUR «خروج»: after a `leave()`, the room closes with
           * colyseus's consented code only once the game-server has run its `onLeave` for it (the
           * seat freed, a host's table closed). Any other code — a dead socket's 1006, nothing at
           * all — says the server may still hold the seat, so it says nothing here.
           */
          if (leftConsented && code === CONSENTED_CLOSE_CODE) handlers.onLeaveHeard?.(r.roomId);
          if (finished && !closedByUs) handlers.onRoomGone?.();
        });
        r.onMessage("turn", (turn: TurnInfo) => handlers.onTurn(turn));
        r.onMessage("turnEnd", () => handlers.onTurnEnd());
        r.onMessage("clock", (clock: ClockInfo) => handlers.onClock?.(clock));
        r.onMessage("paused", (paused: PausedInfo) => handlers.onPaused(paused));
        r.onMessage("resumed", () => handlers.onResumed());
        // The verdict on a «خروج بدون بازگشت» we just sent — resolved into that call's own promise
        // (see `leaveForever`), which is still awaiting it with the socket deliberately held open.
        r.onMessage("leave-penalty", (info: LeavePenaltyInfo) => leaveAck?.(info));
        r.onMessage("left", (info: LeftInfo) => {
          /*
           * ⚠️ MY OWN IDLE REMOVAL ENDS THIS CONNECTION — RELEASE IT, LIKE `gameover`.
           *
           * This is what produced the reported SECOND notice. An idle removal does NOT close the
           * socket, so the removed player's own client receives this frame about its own seat and
           * goes on being MANAGED. When the socket does close (the table finishes, or the room
           * disposes after the deserted sweep) the lifecycle runs a doomed `client.reconnect`, the
           * matchmaker refuses it, and `ConnectionOverlay` paints its `seatExpired` state — 🤖
           * «جات به ربات سپرده شد» — at z-70, on top of the z-62 «کنار گذاشته شدی» notice already
           * on screen. Two notices for one event, the second one shorter and later, exactly as
           * reported.
           *
           * `table-closed`, `table-expired` and `gameover` each already call `release()` for this
           * same reason (the note on the table-closed handler below records the identical bug on
           * the waiting-room path). Idle removal simply never got the same treatment. There is
           * nothing left to reconnect TO: the removal is permanent server-side.
           *
           * Game-agnostic on purpose — the double notice was reachable on every game that can
           * idle-remove a still-connected player (تخته نرد on any table, and every card game on a
           * public one). Only chess is exempt, and only because it has no idle rule at all.
           */
          if (info.reason === "idle" && mySeat !== null && info.seat === mySeat) managed.release();
          handlers.onLeft(info);
        });
        // CHESS ONLY (Phase 3c) — the reconnecting-grace overlay + the claim prompt.
        r.onMessage("reconnecting", (info: ReconnectingInfo) => handlers.onReconnecting?.(info));
        r.onMessage("claimable", (info: ClaimableInfo) => handlers.onClaimable?.(info));
        r.onMessage("table-closed", (msg: { by?: string }) => {
          // The table is GONE — stop managing this connection so the socket close that follows
          // (the server's `disconnect()`) does NOT trigger a reconnect. Without this, the doomed
          // rejoin fails and flashes «اتصال خیلی طول کشید» OVER the "table closed" notice (the
          // reported double-notice on a waiting-room close). Same as `gameover`.
          managed.release();
          handlers.onTableClosed?.(typeof msg?.by === "string" ? msg.by : "");
        });
        r.onMessage("table-expired", () => {
          managed.release();
          handlers.onTableExpired?.();
        });
        // Another player reclaimed their bot-held seat (online re-join). The roster/state frames
        // already show the bot become a human again; this just acknowledges the message.
        r.onMessage("rejoined", () => handlers.onRejoined?.());
        r.onMessage(
          "seat-token-minted",
          (msg: { seat: number; toUserId: string; roomId: string; token: string; game?: string }) =>
            handlers.onSeatTokenMinted?.(msg),
        );
        r.onMessage("state", (msg: StateMessage<M, V>) => {
          gameStarted = true; // redacted views exist only after the deal
          handlers.onState(msg.view, msg.legal);
        });
        r.onMessage("gameover", (result: GameResult) => {
          finished = true;
          managed.release(); // terminal — a finished match must never auto-rejoin
          handlers.onGameOver(result);
        });
        // A bare transport error (a dead WebSocket has no server code/reason →
        // "Error undefined") is a dropped socket the lifecycle already owns
        // (onLeave → reconnect); routing it would race the Persian reconnect
        // overlay with raw English. Only a real server message is surfaced.
        r.onError((code: number, message?: string) => {
          // A NEWER COPY OF THE APP TOOK THIS SEAT (card-74): final, ahead of the close that follows.
          if (code === SEAT_TAKEN_OVER_CLOSE_CODE) {
            takenOverOn(r);
            return;
          }
          if (message) handlers.onError(message);
          else console.error(`[online:${opts.room}] socket error ${code} — reconnecting`);
        });
      },
    });

    return {
      send: (move) => managed.send("move", move),
      sendSettings: (settings) => managed.send("settings", settings),
      start: (intent) => managed.send("start", intent),
      setTeams: (order) => managed.send("set-teams", { order }),
      resume: () => managed.send("resume"),
      claim: () => managed.send("claim"), // CHESS — claim the win/draw while the opponent is gone
      leave: () => {
        closedByUs = true;
        leftConsented = true;
        managed.leave();
      },
      drop: () => {
        closedByUs = true;
        managed.drop();
      },
      requestRematch: () => managed.send("rematch", {}),
      leaveWaiting: () => managed.send("leave-waiting"),
      leaveForever: async () => {
        // The verdict's listener goes up FIRST and synchronously, exactly as it always did — the
        // wait below must not open a gap in which the answer could arrive unheard.
        let settle!: (info: LeavePenaltyInfo | null) => void;
        const ack = new Promise<LeavePenaltyInfo | null>((resolve) => {
          settle = resolve;
        });
        leaveAck = (info) => settle(info);
        // A DEAD SOCKET CANNOT CARRY THE FRAME (card-a5 item 1): «خروج» confirmed while the
        // connection is down waits — bounded — for the lifecycle's own reconnect to bring it back,
        // and only then says it. Never back: nothing was said, nothing is charged, the exit happens.
        if (!(await managed.whenLive(LEAVE_LIVE_WAIT_MS))) {
          leaveAck = null;
          closedByUs = true;
          managed.leave();
          throw new LeaveNotDeliveredError();
        }
        // Mark the exit permanent server-side and wait for its verdict — «this was your 2nd leave
        // today» is the part that changes behaviour, and it can only be said before we disconnect.
        // Bounded: an unanswered server (an old build, a dead socket) costs the notice, never the
        // exit.
        window.setTimeout(() => settle(null), LEAVE_ACK_TIMEOUT_MS);
        managed.send("leave-forever");
        const info = await ack;
        leaveAck = null;
        closedByUs = true;
        managed.leave();
        return info;
      },
      mintSeatToken: (seat, toUserId) => managed.send("mint-seat-token", { seat, toUserId }),
      kick: (seat) => managed.send("kick", { seat }),
    };
  };
}

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/**
 * Did a `rejoin` fail because the SEAT IS GONE — the table is over, the room disposed, or the
 * reservation provably absent past the patience window — rather than because the network failed?
 * (card-a5 item 1: the app's own straight-back return drops its ticket only on this.)
 */
export function isDefinitiveRejoinFailure(error: unknown): boolean {
  return error instanceof TableOverError || isSeatGone(error);
}

/**
 * `4214 reconnection token invalid or expired.` — Colyseus holds no reservation for this token.
 *
 * That is ALSO what it answers while the reservation does not exist YET: the room mints it in
 * `onLeave`, which runs only once the server has noticed the old socket is gone. So inside
 * {@link RECONNECT_TOO_EARLY_WINDOW_MS} this code means «not yet» (see the reacquire above), and
 * only past it «gone». Keyed on the CODE: `4212 room "X" has been disposed.` is never early.
 */
function isTooEarly(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "code" in error &&
    (error as { code?: unknown }).code === 4214
  );
}

/**
 * `seat reservation expired.` AT THE UPGRADE — the reservation this request was granted had already
 * been consumed by ANOTHER socket: the loser of a duplicated reconnect (CLAUDE.md, standing verdict:
 * refused non-terminally, the seat is not lost). The ws-transport throws it as a plain `Error`, so
 * colyseus.js hands it over by its MESSAGE, and the close that follows carries
 * `WS_CLOSE_WITH_ERROR` (4002); either may arrive first.
 *
 * On the «ادامه بازی» door that other socket is the table the player LEFT, its reconnect landing late:
 * a moment later it is closed the way a drop closes and the seat is re-minted under its ROTATED
 * token. So here it is «not yet», exactly like a 4214 — never «the ticket is dead».
 */
function isReservationLost(error: unknown): boolean {
  const code =
    typeof error === "object" && error !== null && "code" in error
      ? (error as { code?: unknown }).code
      : undefined;
  return code === 4002 || /seat reservation expired/i.test(messageOf(error));
}

/**
 * Whether a failed `client.reconnect` means the SEAT IS GONE (vs a transient
 * network failure worth retrying). Colyseus answers a reconnect request it
 * UNDERSTOOD but refused with a matchmake protocol error (codes 4210–4215:
 * `room "X" has been disposed.` / `reconnection token invalid or expired.`);
 * network-level failures (server briefly unreachable) carry no matchmake code
 * and stay retryable. ⚠️ A 4214 reaches this only once {@link isTooEarly}'s window has passed.
 */
function isSeatGone(error: unknown): boolean {
  const code =
    typeof error === "object" && error !== null && "code" in error
      ? (error as { code?: unknown }).code
      : undefined;
  if (typeof code === "number" && code >= 4210 && code < 4300) return true;
  return /has been disposed|invalid or expired|not found/i.test(messageOf(error));
}
